import { useMemo, useState, type ReactElement } from 'react'
import { MOCK_VERSIONS } from 'app/components/ops-prompts/mockData'
import { lineDiff, type DiffLine } from 'app/components/ops-prompts/lineDiff'
import { cn } from 'app/lib/utils'
import { foldDiff } from './foldDiff'
import { changedStages, runLabels } from './runMeta'
import type { PipelineStage, Run } from './types'

const STAGE_NAME: Record<PipelineStage, string> = {
  overview: `Overview`,
  people: `People`,
  appointment: `Appointment`,
  outcome: `Outcome`,
  validation: `Validation`,
}

const ROW: Record<DiffLine[`type`], string> = {
  add: `bg-green-50 text-green-900`,
  remove: `bg-red-50 text-red-900`,
  context: `text-default`,
}
const SIGN: Record<DiffLine[`type`], string> = { add: `+`, remove: `−`, context: ` ` }

function Lines({ lines }: { lines: DiffLine[] }): ReactElement {
  return (
    <>
      {lines.map((line, index) => (
        // Diff rows never reorder within one diff.
        // oxlint-disable-next-line react/no-array-index-key
        <div key={index} className={cn(`flex`, ROW[line.type])}>
          <span className="w-9 shrink-0 select-none px-1.5 text-right text-minimal">
            {line.newNumber ?? line.oldNumber ?? ``}
          </span>
          <span className="w-4 shrink-0 select-none text-center">{SIGN[line.type]}</span>
          <span className="whitespace-pre-wrap break-words pr-3">{line.text}</span>
        </div>
      ))}
    </>
  )
}

function FoldedDiff({ before, after }: { before: string; after: string }): ReactElement {
  const blocks = useMemo(() => foldDiff(lineDiff(before, after)), [before, after])
  const [open, setOpen] = useState<Set<number>>(new Set())
  return (
    <div className="overflow-x-auto rounded-md border border-default bg-surface font-mono text-body-xs leading-relaxed">
      {blocks.map((block, index) =>
        block.kind === `lines` || open.has(index) ? (
          // oxlint-disable-next-line react/no-array-index-key
          <Lines key={index} lines={block.lines} />
        ) : (
          <button
            // oxlint-disable-next-line react/no-array-index-key
            key={index}
            type="button"
            onClick={() => setOpen((prev) => new Set(prev).add(index))}
            className="block w-full bg-surface-subtle px-3 py-1 text-left font-sans text-caption-sm text-subtle hover:text-strong"
          >
            ⋯ {block.lines.length} unchanged {block.lines.length === 1 ? `line` : `lines`}
          </button>
        ),
      )}
    </div>
  )
}

const body = (key: PipelineStage | `single`, version: number) =>
  MOCK_VERSIONS.find((entry) => entry.promptKey === key && entry.version === version)

/** A pipeline change: the single prompt differs from the stages as a whole, not line by line. */
function PipelineChange({ run }: { run: Run }): ReactElement {
  const { candidate, baseline } = run
  if (candidate.single !== undefined && baseline.single !== undefined) {
    const before = body(`single`, baseline.single)
    const after = body(`single`, candidate.single)
    return before && after ? (
      <FoldedDiff before={before.body} after={after.body} />
    ) : (
      <p className="text-caption-sm text-subtle">Prompt text for one of these versions is not available.</p>
    )
  }
  const single = body(`single`, (candidate.single ?? baseline.single)!)
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-default">
        One side runs the single prompt in one call; the other runs the 5 stage prompts in sequence. Both end with the
        same stage 5 rules.
        {single?.changeSummary && <span className="text-subtle"> {single.changeSummary}</span>}
      </p>
      {single && (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-default bg-surface p-3 font-mono text-body-xs leading-relaxed text-default">
          {single.body}
        </pre>
      )}
    </div>
  )
}

/** What the candidate prompt changed, per stage, as a folded diff against the baseline. */
export function PromptChanges({ run }: { run: Run }): ReactElement {
  const changed = changedStages(run)
  if (run.candidate.single !== undefined || run.baseline.single !== undefined) {
    const labels = runLabels(run)
    const same = run.candidate.single === run.baseline.single
    return (
      <details className="group rounded-lg border border-default bg-surface-raised">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-strong">
          What changed in the prompt
          <span className="text-caption-sm font-normal text-subtle">
            {same ? `Nothing: both sides ran the same prompt` : `${labels.baseline} → ${labels.candidate}`}
          </span>
        </summary>
        {!same && (
          <div className="border-t border-subtle px-4 py-3">
            <PipelineChange run={run} />
          </div>
        )}
      </details>
    )
  }
  return (
    <details className="group rounded-lg border border-default bg-surface-raised">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-strong">
        What changed in the prompt
        <span className="text-caption-sm font-normal text-subtle">
          {changed.length === 0
            ? `Nothing: both sides ran the same prompts`
            : changed
                .map((stage) => `${STAGE_NAME[stage]} v${run.baseline[stage]} → v${run.candidate[stage]}`)
                .join(` · `)}
        </span>
      </summary>
      {changed.length > 0 && (
        <div className="space-y-4 border-t border-subtle px-4 py-3">
          {changed.map((stage) => {
            const before = body(stage, run.baseline[stage])
            const after = body(stage, run.candidate[stage])
            return (
              <div key={stage}>
                <div className="mb-1.5 text-sm text-default">
                  <span className="font-medium">
                    {STAGE_NAME[stage]} v{run.baseline[stage]} → v{run.candidate[stage]}
                  </span>
                  {after?.changeSummary && <span className="text-subtle"> · {after.changeSummary}</span>}
                </div>
                {before && after ? (
                  <FoldedDiff before={before.body} after={after.body} />
                ) : (
                  <p className="text-caption-sm text-subtle">Prompt text for one of these versions is not available.</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </details>
  )
}
